import React, { type FunctionComponent, useMemo } from 'react'
import styled from '@emotion/styled'
import { spacing } from '@retailer-platform/shared-components'
import { type Moment } from 'moment'
import { DayWeekToggle } from 'gin-and-tonic/containers/day-week-toggle/DayWeekToggle'
import { ShiftsSingleDatePicker } from 'legacy/components/Dates/ShiftsSingleDatePicker'
import { WeekPicker } from 'legacy/components/Dates/WeekPicker'
import { StoreGroupDropdown } from 'domains/aggregate-store-performance/components/store-group-dropdown/StoreGroupDropdown'
import { type StoreGroupDropdownOption } from 'domains/aggregate-store-performance/components/store-group-dropdown/utils/StoreGroupDropdown.types'
import { ExportButton } from 'domains/aggregate-store-performance/components/export-button/ExportButton'
import { type StoreMetric } from 'domains/aggregate-store-performance/types/AggregateStorePerformance.types'
import {
  FilterPills,
  type Filter,
} from 'domains/aggregate-store-performance/components/filter-pills/FilterPills'
import { UpdatedAt } from 'domains/aggregate-store-performance/components/updated-at/UpdatedAt'
import {
  momentToQueryStringDate,
  getMinWeekOffset,
  isDateOutOfRange,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import { MAX_WEEK_OFFSET } from 'domains/aggregate-store-performance/utils/constants/AggregateStorePerformanceOverviewPage.constants'
import { FiltersDropdown } from 'domains/aggregate-store-performance/components/filters-dropdown/FiltersDropdown'
import {
  type FiltersDropdownOption,
  type GroupedFiltersDropdownOptions,
} from 'domains/aggregate-store-performance/components/filters-dropdown/utils/FiltersDropdown.types'
import { useDataPastCutoffDate } from 'domains/aggregate-store-performance/utils/hooks/useDataPastCutoffDate.hook'

const INPUT_HEIGHT = '38px'

const FilterBarContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  margin: `${spacing.X4} ${spacing.X20} ${spacing.X4} ${spacing.X20}`,
})

const Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: spacing.X4,
  ':last-of-type': {
    marginBottom: 0,
  },
})

const LeftFilterOptions = styled.div({
  display: 'flex',
  flexDirection: 'row',
  marginRight: 'auto',
})

const RightFilterOptions = styled.div({
  display: 'flex',
  flexDirection: 'row',
})

const FilterOption = styled.div({
  marginLeft: spacing.X16,
})

interface FilterBarProps {
  isStoreGroupsLoading: boolean
  storeGroups: StoreGroupDropdownOption[]
  selectedStoreGroups: StoreGroupDropdownOption[]
  setSelectedStoreGroups: (storeGroups: StoreGroupDropdownOption[]) => void
  refetchStoreGroups: () => void
  isWeek: boolean
  onToggleDayWeek: (isWeek: boolean) => void
  handleDateChange: (date: Moment | string) => void
  startDate: Moment
  perStoreMetrics: StoreMetric[]
  updatedAt: Moment
  now: Moment
  filters: GroupedFiltersDropdownOptions[]
  selectedFilters: FiltersDropdownOption[]
  setSelectedFilters: (filters: FiltersDropdownOption[]) => void
  isFiltersMenuOpen: boolean
  onFilterMenuClose: () => void
}

export const FilterBar: FunctionComponent<FilterBarProps> = ({
  isStoreGroupsLoading,
  storeGroups,
  selectedStoreGroups,
  setSelectedStoreGroups,
  refetchStoreGroups,
  isWeek,
  onToggleDayWeek,
  handleDateChange,
  startDate,
  perStoreMetrics,
  updatedAt,
  now,
  filters,
  selectedFilters,
  setSelectedFilters,
  isFiltersMenuOpen,
  onFilterMenuClose,
}) => {
  const dataPastCutoffDate = useDataPastCutoffDate()

  const activeFilters: Filter[] = useMemo(() => {
    const storeGroupFilters: Filter[] = selectedStoreGroups.map(storeGroup => ({
      name: storeGroup.name,
      id: storeGroup.value,
      onRemove: () => {
        const newSelectedStoreGroups = selectedStoreGroups.filter(
          selectedStoreGroup => selectedStoreGroup.value !== storeGroup.value
        )
        setSelectedStoreGroups(newSelectedStoreGroups)
      },
    }))

    const filters: Filter[] = selectedFilters.map(filter => ({
      name: filter.label,
      id: filter.value,
      onRemove: () => {
        const newSelectedFilters = selectedFilters.filter(
          selectedFilter => selectedFilter.value !== filter.value
        )
        setSelectedFilters(newSelectedFilters)
      },
    }))

    return [...storeGroupFilters, ...filters]
  }, [selectedStoreGroups, setSelectedStoreGroups, selectedFilters, setSelectedFilters])

  const date = momentToQueryStringDate(startDate)
  const minWeekOffset = getMinWeekOffset(now, dataPastCutoffDate)
  const isOutsideRange = useMemo(
    () => isDateOutOfRange(now, dataPastCutoffDate),
    [now, dataPastCutoffDate]
  )

  return (
    <FilterBarContainer>
      <Row>
        <LeftFilterOptions>
          <StoreGroupDropdown
            isLoading={isStoreGroupsLoading}
            selectedStoreGroups={selectedStoreGroups}
            onSelectionChange={setSelectedStoreGroups}
            refetchStoreGroups={refetchStoreGroups}
            storeGroups={storeGroups}
          />

          <FilterOption>
            <FiltersDropdown
              filters={filters}
              selectedFilters={selectedFilters}
              onSelectionChange={setSelectedFilters}
              isLoading={false}
              showMenu={isFiltersMenuOpen}
              onMenuClose={onFilterMenuClose}
            />
          </FilterOption>
        </LeftFilterOptions>
        <RightFilterOptions>
          <FilterOption>
            {isWeek ? (
              <WeekPicker
                inputHeight={INPUT_HEIGHT}
                week={date}
                updateQueryWeek={handleDateChange}
                minWeekOffset={minWeekOffset}
                maxWeekOffset={MAX_WEEK_OFFSET}
              />
            ) : (
              <ShiftsSingleDatePicker
                inputHeight={INPUT_HEIGHT}
                date={date}
                isOutsideRange={isOutsideRange}
                onDateChange={handleDateChange}
                displayFormat={'dddd, MMM D'}
              />
            )}
          </FilterOption>
          <FilterOption>
            <DayWeekToggle isWeek={isWeek} handleToggle={onToggleDayWeek} />
          </FilterOption>
          <FilterOption>
            <ExportButton data={perStoreMetrics} startDate={startDate} isWeek={isWeek} />
          </FilterOption>
        </RightFilterOptions>
      </Row>
      <Row>
        <LeftFilterOptions>
          <FilterPills filters={activeFilters} />
        </LeftFilterOptions>
        <RightFilterOptions style={{ flexShrink: 0 }}>
          <UpdatedAt updatedAt={updatedAt} />
        </RightFilterOptions>
      </Row>
    </FilterBarContainer>
  )
}
